/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { memo } from 'react';
import type { MutableRefObject } from 'react';

import { CardStatusTag, Typography, Typography2, breakpoints } from '@coursera/cds-core';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import { PRODUCT_CARD_IMAGE_DEFAULT_VALUES } from 'bundles/collections-carousel/constants';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { Partners } from 'bundles/product-card/components/legacy/Partners';
import ProductCardCourseraPlus from 'bundles/product-card/components/legacy/ProductCardCourseraPlus';
import ProductCardDegreeOrCredit from 'bundles/product-card/components/legacy/ProductCardDegreeOrCredit';
import ProductCardImage from 'bundles/product-card/components/legacy/ProductCardImage';
import type { ProductCardType } from 'bundles/product-card/components/legacy/types/ProductCard';
import { capitalizeAndTranslateEachWordInEntityType } from 'bundles/product-card/components/utils/productCardV2Utils';
import useGenAiBadges from 'bundles/product-card/hooks/useGenAiBadges';
import { useProductCardLeftOrRightTag } from 'bundles/product-card/hooks/useProductCardLeftOrRightTag';
import { getPartnerNames } from 'bundles/product-card/utils/productCardUtils';
import isCreditOffered from 'bundles/search-common/components/search-cards/isCreditOffered';
import isPremiumProduct from 'bundles/search-common/components/search-cards/isPremiumProduct';
import { LEARNING_PRODUCTS, getTranslatedDifficulty } from 'bundles/search-common/constants';

import _t from 'i18n!nls/product-card';

const styles = {
  cardContainer: css`
    min-height: 200px;
    height: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
  `,
  cardImage: (overrideHeight?: number) => css`
    height: ${overrideHeight || PRODUCT_CARD_IMAGE_DEFAULT_VALUES.height}px;
    overflow: hidden;

    > div {
      width: 100%;
    }
  `,
  cardContent: css`
    padding: 16px;
    display: flex;
    flex: 1 0 auto;
    flex-direction: column;
    row-gap: 16px;
    justify-content: space-between;
  `,
  cardContentBody: css`
    display: grid;
    row-gap: 8px;
  `,
  skillsContainer: css`
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
  `,
  skillsYoullGain: () => css`
    color: var(--cds-color-neutral-primary);
  `,
  partnerContainer: css`
    display: flex;
    flex-wrap: wrap;
  `,
  badge: () => css`
    border-radius: 4px;
    position: absolute;
    right: 8px;
    top: 8px;
    box-shadow: 0 0 6px rgb(0 0 0 / 25%);
  `,
  leftAlignProductCardTags: css`
    ${breakpoints.up('sm')} {
      right: unset;
      left: 8px;
    }
  `,
};

const getProductInfo = (
  productDifficultyLevel?: string,
  productDuration?: string,
  entityType?: (typeof LEARNING_PRODUCTS)[keyof typeof LEARNING_PRODUCTS]
) => {
  const info = [];
  if (productDifficultyLevel) {
    info.push(getTranslatedDifficulty(productDifficultyLevel));
  }
  if (entityType) {
    info.push(capitalizeAndTranslateEachWordInEntityType(entityType));
  }
  if (productDuration) {
    info.push(productDuration);
  }

  return info.join(' · ');
};

/** @deprecated legacy product cards exist for support but should not be introduced. */
export const ProductCard = ({
  cardProps,
  cardPosition,
  ownsCourseraPlus,
  ownsCourseraLite,
  productCardImageProps,
  metadataTags,
  cardHeader,
  skills,
  reviews,
  truncatePartnerName,
  titleTrackingData,
  slug,
  eventingV3TrackingData,
}: ProductCardType) => {
  const {
    id,
    name,
    imageUrl,
    productType,
    productDifficultyLevel,
    productDuration,
    partners,
    partnerLogos,
    isCostFree,
    isPartOfCourseraPlus,
    session,
    cobrandingEnabled,
  } = cardProps;

  const cardRef: MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_product_card',
    eventingV3TrackingData
  );

  const { badges } = useGenAiBadges(slug);

  const productCardLeftOrRightTag = useProductCardLeftOrRightTag();
  const leftAlignTag = productCardLeftOrRightTag === 'left';

  return (
    <div css={styles.cardContainer} data-e2e="ProductCard" ref={cardRef}>
      <div css={styles.cardImage(productCardImageProps?.height)}>
        <ProductCardImage
          id={id}
          imageUrl={imageUrl}
          partnerLogos={partnerLogos}
          cardPosition={cardPosition}
          productCardImageProps={productCardImageProps}
        />
      </div>
      {isCostFree && (
        <div css={[styles.badge, leftAlignTag && styles.leftAlignProductCardTags]}>
          <CardStatusTag>{_t('Free')}</CardStatusTag>
        </div>
      )}
      {badges.map((badge) => (
        <div css={[styles.badge, leftAlignTag && styles.leftAlignProductCardTags]}>{badge}</div>
      ))}
      <div css={styles.cardContent} aria-hidden>
        <div css={styles.cardContentBody}>
          {partners && partners.length && (
            // partner-logo-legacy used to target the partner logos for Product Card in Carousels on LOHP experiment. Ticket for cleanup: https://coursera.atlassian.net/browse/CORDISC-1091
            <div css={styles.partnerContainer} className="partner-logo-lohp-legacy">
              <Partners
                partners={partners && getPartnerNames(partners)}
                partnerLogos={partnerLogos}
                cobrandingEnabled={cobrandingEnabled}
                truncateName={truncatePartnerName}
              />
            </div>
          )}
          {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
          {cardHeader || <Typography variant="h2semibold">{name}</Typography>}
          {/*
              Summary: titleTrackingData added in order to make this card behave like the new CDS cards in experiments
              Analysis: https://docs.google.com/document/d/1IwonnGDcN9nh-cxkn8NPspKS4S8WcBuHHAinToKQzPQ/edit#heading=h.azyrzx5itcu
              Slack thread: https://coursera.slack.com/archives/C04UJEGH37U/p1686754157564539
             */}
          {titleTrackingData && (
            <TrackedDiv
              trackingName={titleTrackingData.trackingName}
              withVisibilityTracking={true}
              data={titleTrackingData.data}
            />
          )}

          {skills}
          {metadataTags}
          {session && (
            <Typography2 component="p" variant="bodySecondary" color="supportText" css={styles.skillsContainer}>
              <span css={styles.skillsYoullGain}>
                <b>{session}</b>
              </span>
            </Typography2>
          )}
        </div>
        <div>
          <ProductCardCourseraPlus
            isPartOfCourseraPlus={Boolean(isPartOfCourseraPlus)}
            ownsCourseraLite={Boolean(ownsCourseraLite)}
            ownsCourseraPlus={Boolean(ownsCourseraPlus)}
            productType={productType}
          />
          {productType && isPremiumProduct(productType) ? (
            <ProductCardDegreeOrCredit
              isDegree={productType === LEARNING_PRODUCTS.Degree}
              showCreditOffered={isCreditOffered(productType)}
            />
          ) : (
            reviews
          )}
          <Typography2 component="p" variant="bodySecondary" color="supportText">
            {getProductInfo(productDifficultyLevel, productDuration, productType)}
          </Typography2>
        </div>
      </div>
    </div>
  );
};

/** @deprecated (Memoized) legacy product cards exist for support but should not be introduced. */
export default memo(ProductCard);
